import {
  CreateSimulationGroupRequest,
  MetaData,
  ParkingAndChargingLocations,
  Simulation,
  SimulationError,
  SimulationGroup,
  VehicleRange,
  CheckpointSet,
} from '@thive/fddm-types';

import { ErrorMessageServiceConnector } from '@/modules/api/ErrorMessageServiceConnector/ErrorMessageServiceConnector';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { SimulationServiceConnector } from '@/modules/api/SimulationServiceConnector/SimulationServiceConnector';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { getVehicleAmountPreferenceKey } from '@/modules/floorplanDesign/preferences';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { VehiclesUtil } from '@/modules/vehicles/vehiclesUtil';
import { logger } from '@common/logging/logger';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';

import { SimulationUtil } from '../SimulationUtil';
import { DeepPartial, useSimulationGroupStore } from '../useSimulationGroupStore';
import { ISimulationService } from './ISimulationService';

export class SimulationServiceImpl implements ISimulationService {
  private simulationStore: typeof useSimulationGroupStore;
  private fddmStore: typeof useFloorplanDesignStore;
  private vehicleStore: typeof useVehicleStore;

  public constructor(
    simulationStore: typeof useSimulationGroupStore,
    fddmStore: typeof useFloorplanDesignStore,
    vehicleStore: typeof useVehicleStore,
  ) {
    this.simulationStore = simulationStore;
    this.fddmStore = fddmStore;
    this.vehicleStore = vehicleStore;
  }

  //
  // CreateSimulationGroupRequest usecases
  //

  useDraft() {
    return this.simulationStore((s) => s.data);
  }

  getDraft(): CreateSimulationGroupRequest {
    return this.simulationStore.getState().getDraft();
  }

  createDraft(name: string): CreateSimulationGroupRequest {
    const draft = this.createSimulationGroupRequest(name);
    this.simulationStore.getState().setDraft(draft);
    return draft;
  }

  createDraftFromRecord(record: SimulationGroup, name: string): CreateSimulationGroupRequest {
    const draft = this.createSimulationGroupRequest(name);

    draft.config.duration = record.config.duration / 3600;

    // First reset
    record.config.vehicleRanges.forEach((vr) => {
      const draftVr = draft.config.vehicleRanges.find((e) => e.vehicleType === vr.vehicleType);
      if (draftVr) {
        draftVr.range = [];
      }
    });

    // Second initialize
    record.config.vehicleRanges.forEach((vr) => {
      const draftVr = draft.config.vehicleRanges.find((e) => e.vehicleType === vr.vehicleType);
      if (draftVr) {
        vr.range.forEach((value) => {
          draftVr.range.push(value);
        });
      }
    });

    this.simulationStore.getState().setDraft(draft);
    return draft;
  }

  updateDraft(value: DeepPartial<CreateSimulationGroupRequest>): void {
    this.simulationStore.getState().updateDraft(value);
  }

  //
  // Utility usecases
  //

  getRecommendedSimulationTime(): number {
    return this.fddmStore
      .getState()
      .getFlows()
      .reduce((max, flow) => {
        if (flow.type === 'CONSOLIDATION_RETRIEVAL') {
          const retrievalStartDelayInHours =
            flow.consolidationRetrievalExtension?.retrievalStartDelayInHours ?? 0;
          return Math.max(max, retrievalStartDelayInHours + 1);
        }
        return max;
      }, 0);
  }

  getMaxCapacity(vehicleType: string): number {
    const maxCapacityFromStationaries =
      this.getNrStationariesPerVehicleType().get(vehicleType) ?? 0;
    return this.getMaxCapacityByVehicleType(vehicleType, maxCapacityFromStationaries);
  }

  //
  // Simulation API usecases
  //

  async getSimulationGroups(): Promise<SimulationGroup[]> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return [];
    }

    try {
      return await SimulationServiceConnector.getInstance().getSimulationGroups(
        metaData.projectId,
        metaData.alternativeId,
      );
    } catch (err) {
      logger.error('Failed to get simulationgroups', err);
      throw err;
    }
  }

  async getSimulationGroup(simulationGroupId: string): Promise<SimulationGroup> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      throw new Error('Expected loaded simulation context metadata.');
    }

    try {
      return await SimulationServiceConnector.getInstance().getSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
      );
    } catch (err) {
      logger.error('Failed to get simulation group', err);
      throw err;
    }
  }

  async getSimulationsInGroup(simulationGroupId: string): Promise<Simulation[]> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return [];
    }

    try {
      return await SimulationServiceConnector.getInstance().getSimulationsInGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
      );
    } catch (err) {
      logger.error('Failed to get simulations in group', err);
      throw err;
    }
  }

  async createSimulationGroup(): Promise<SimulationGroup | null> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return null;
    }

    try {
      const result = await SimulationServiceConnector.getInstance().createSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        this.getDraft(),
      );

      if (result.errors && result.errors.length > 0) {
        this.displayErrorMessages(result.errors);
        return null;
      }

      return result;
    } catch (err) {
      logger.error('Failed to create simulation', err);
      throw err;
    }
  }

  async reRunSimulationGroup(simulationGroupId: string): Promise<SimulationGroup | null> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return null;
    }

    try {
      const result = await SimulationServiceConnector.getInstance().reRunSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
      );

      if (result.errors && result.errors.length > 0) {
        this.displayErrorMessages(result.errors);
        return null;
      }

      return result;
    } catch (err) {
      logger.error('Failed to re-run simulation group', err);
      throw err;
    }
  }

  async renameSimulationGroup(simulationGroupId: string, name: string): Promise<void> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return;
    }

    try {
      const result = await SimulationServiceConnector.getInstance().renameSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
        name,
      );

      if (result.errors && result.errors.length > 0) {
        this.displayErrorMessages(result.errors);
      }
    } catch (err) {
      logger.error('Failed to rename simulation group', err);
      throw err;
    }
  }

  async deleteSimulationGroup(simulationGroupId: string): Promise<void> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return;
    }

    try {
      await SimulationServiceConnector.getInstance().deleteSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
      );
    } catch (err) {
      logger.error('Failed to delete simulation group', err);
      throw err;
    }
  }

  async abortSimulationGroup(simulationGroupId: string): Promise<void> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return;
    }

    try {
      await SimulationServiceConnector.getInstance().abortSimulationGroup(
        metaData.projectId,
        metaData.alternativeId,
        simulationGroupId,
      );
    } catch (err) {
      logger.error('Failed to abort simulation group', err);
      throw err;
    }
  }

  async downloadSimulationFiles(simulationRunId: string, simulationName: string): Promise<void> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return;
    }

    try {
      await SimulationServiceConnector.getInstance().downloadSimulationFiles(
        metaData.projectId,
        simulationRunId,
        metaData.alternativeName,
        simulationName,
      );
    } catch (err) {
      logger.error('Failed to download simulation files', err);
      throw err;
    }
  }

  async downloadFmsConfig(): Promise<void> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return;
    }

    try {
      await SimulationServiceConnector.getInstance().downloadFmsConfig(
        metaData.projectId,
        metaData.alternativeId,
        this.getDraft(),
      );
    } catch (err) {
      logger.error('Failed to download fms config', err);
      throw err;
    }
  }

  getCustomOrderProfileUrl(): string | undefined {
    return this.fddmStore
      .getState()
      .getFileReferences()
      .find((file) => file.type === 'CUSTOM_ORDER_PROFILE')?.url;
  }

  getCustomFmsConfigUrl(): string | undefined {
    return this.fddmStore
      .getState()
      .getFileReferences()
      .find((file) => file.type === 'CUSTOM_FMS_CONFIG')?.url;
  }

  async getSimulationFiles(): Promise<{
    fmsConfigFiles: object[];
    fmsConfigFileNames: string[];
    orderProfileFiles: Blob[];
    orderProfileFileNames: string[];
  }> {
    const metaData = this.getFloorplanRevisionMetaData();

    if (!this.hasSimulationContext(metaData)) {
      return {
        fmsConfigFiles: [],
        fmsConfigFileNames: [],
        orderProfileFiles: [],
        orderProfileFileNames: [],
      };
    }

    const storageBasePath = buildRevisionFileStorageBasePath(metaData);
    if (!storageBasePath) {
      throw new Error('Could not resolve the floorplan revision storage path.');
    }

    const fileReferences = this.fddmStore.getState().getFileReferences();
    const fmsConfigReferences = fileReferences.filter((file) => file.type === 'CUSTOM_FMS_CONFIG');
    const orderProfileReferences = fileReferences.filter(
      (file) => file.type === 'CUSTOM_ORDER_PROFILE',
    );

    try {
      const fmsConfigFiles = await Promise.all(
        fmsConfigReferences.map((fileReference) =>
          FileServiceConnector.getInstance().getJsonFileByUrl(
            `${PROJECT_FILES_ROOT_CONTAINER}?filename=${storageBasePath}/${fileReference.url}`,
          ),
        ),
      );

      const orderProfileFiles = await Promise.all(
        orderProfileReferences.map((fileReference) =>
          FileServiceConnector.getInstance().getBlobFileByUrl(
            `${PROJECT_FILES_ROOT_CONTAINER}?filename=${storageBasePath}/${fileReference.url}`,
          ),
        ),
      );

      return {
        fmsConfigFiles,
        fmsConfigFileNames: fmsConfigReferences.map((fileReference) =>
          SimulationUtil.getFileNameFromUrl(fileReference.url),
        ),
        orderProfileFiles,
        orderProfileFileNames: orderProfileReferences.map((fileReference) =>
          SimulationUtil.getFileNameFromUrl(fileReference.url),
        ),
      };
    } catch (err) {
      logger.error('Failed to get simulation files', err);
      throw err;
    }
  }

  //
  // Utility methods
  //

  private getFloorplanRevisionMetaData(): MetaData {
    return this.fddmStore.getState().getMetaData();
  }

  private hasSimulationContext(metaData: MetaData): boolean {
    return Boolean(metaData.projectId && metaData.alternativeId && metaData.revisionId);
  }

  private createSimulationGroupRequest(name: string): CreateSimulationGroupRequest {
    const { getPreference, getAllAreas, getPois, getModel } = this.fddmStore.getState();
    const floorPlanRevision = getModel();
    const simDuration = getPreference('simulationConfig.duration');
    const obstructionTimeOutInSeconds = getPreference(
      'simulationConfig.fmsConfig.obstructionTimeoutInSeconds',
    );
    const failOnNoRouteFound = getPreference('simulationConfig.fmsConfig.failOnNoRouteFound');
    const manualIntervention = getPreference('simulationConfig.fmsConfig.manualIntervention');
    const distributionStrategy = getPreference('simulationConfig.fmsConfig.distributionStrategy');
    const hopsToBlockBeforeClaimedEndpoint = getPreference(
      'simulationConfig.fmsConfig.hopsToBlockBeforeClaimedEndpoint',
    );
    const maxForwardSpeedPerc = getPreference('simulationConfig.uvtOverrides.maxForwardSpeedPerc');
    const maxReverseSpeedPerc = getPreference('simulationConfig.uvtOverrides.maxReverseSpeedPerc');
    const loadHandlingDurationPerc = getPreference(
      'simulationConfig.uvtOverrides.loadHandlingDurationPerc',
    );

    const parkingAndChargingLocations: ParkingAndChargingLocations[] = [];
    const priorityMap: Map<string, number> = new Map();
    const areas = getAllAreas();
    areas.stationaryAreas
      .filter((area) => area.parkingExtension || area.chargingExtension)
      .forEach((area) => {
        priorityMap.set(
          area.id,
          area.parkingExtension?.priority ?? area.chargingExtension?.priority ?? 1,
        );
      });
    areas.singularStationAreas
      .filter(
        (area) =>
          area.loadHandlingPoiSpecs[0].canBeUsedForParking &&
          area.loadHandlingPoiSpecs[0].parkingExtension,
      )
      .forEach((area) => {
        priorityMap.set(area.id, area.loadHandlingPoiSpecs[0].parkingExtension?.priority ?? 1);
      });
    priorityMap.forEach((priority, areaId) => {
      getPois()
        .filter((poi) => poi.areaId === areaId)
        .forEach((poi) => {
          parkingAndChargingLocations.push({
            name: poi.id,
            priority,
          });
        });
    });

    const vehicleTypes: VehicleRange[] = [];
    this.getNrStationariesPerVehicleType().forEach((v, k) => {
      const maxCapacity = this.getMaxCapacityByVehicleType(k, v);
      const stored = getPreference(getVehicleAmountPreferenceKey(k));
      const preferredAmount = stored > 0 ? stored : maxCapacity;
      vehicleTypes.push({
        vehicleType: k,
        hue: this.getHueForVehicleType(k),
        range: [this.getValidVehicleAmount(preferredAmount, maxCapacity)],
      });
    });

    const customPlaybookFileRef = this.getCustomOrderProfileUrl();

    const checkpointSets: CheckpointSet[] = [];
    floorPlanRevision.design.areas.highwayAreas.forEach((highway) => {
      if (highway.vehicleLimit >= 0) {
        const { highwayPoiSpecs, segments } = highway;

        checkpointSets.push({
          name: highway.id,
          maxVehiclesInSet: highway.vehicleLimit,
          highways: highwayPoiSpecs.flatMap((highwayPoiSpec) => {
            const { vehicleTypeId } = highwayPoiSpec;
            return segments.flatMap((segment) => `${vehicleTypeId}.${segment.id}`);
          }),
          checkpoints: [],
        });
      }
    });

    return {
      name,
      floorPlanRevision: {
        ...floorPlanRevision,
        floorplan: {
          stations: [],
          vehicleLayouts: [],
        },
        tracks: [],
      },
      config: {
        duration: simDuration,
        vehicleRanges: vehicleTypes,
        fmsConfig: {
          name: 'fms-config',
          obstructionTimeOutInSeconds,
          failOnNoRouteFound,
          manualIntervention,
          distributionStrategy,
          parkingAndChargingLocations,
          hopsToBlockBeforeClaimedEndpoint,
          checkpointSets,
        },
        uvtOverrides: {
          maxForwardSpeedPerc,
          maxReverseSpeedPerc,
          loadHandlingDurationPerc,
        },
      },
      customPlaybookFileRef,
    };
  }

  private getHueForVehicleType(vehicleTypeId: string): number {
    const selectedVehicleTypes = this.fddmStore.getState().getVehicleTypes();
    const vehicle = this.vehicleStore
      .getState()
      .allVehicles.getByIds(selectedVehicleTypes.map((e) => e.id))
      .find((v) => v.name === vehicleTypeId);
    return vehicle ? vehicle.hue : 0;
  }

  private getNrStationariesPerVehicleType(): Map<string, number> {
    const result: Map<string, number> = new Map();

    const selectedVehicleTypes = this.fddmStore.getState().getVehicleTypes();
    const uvts = this.vehicleStore
      .getState()
      .allVehicles.getByIds(selectedVehicleTypes.map((e) => e.id));
    const stationaryAreas = this.fddmStore
      .getState()
      .getAllAreas()
      .stationaryAreas.filter((a) => a.chargingExtension || a.parkingExtension);
    const singularStationAreas = this.fddmStore
      .getState()
      .getAllAreas()
      .singularStationAreas.filter((a) => a.loadHandlingPoiSpecs[0].canBeUsedForParking);
    const rackingAreas = this.fddmStore
      .getState()
      .getAllAreas()
      .rackingAreas.filter((a) => a.canBeUsedForParking);
    const rackingAreasInside = rackingAreas.filter((a) => a.rackType === 'ONE_SIDED_INSIDE');
    const rackingAreasAlongside = rackingAreas.filter(
      (a) => a.rackType === 'ONE_SIDED_ALONGSIDE' || a.rackType === 'TWO_SIDED_ALONGSIDE',
    );

    uvts.forEach((uvt) => {
      let count = result.get(uvt.name) ?? 0;
      [...stationaryAreas, ...singularStationAreas, ...rackingAreasInside].forEach((area) => {
        const pois = this.fddmStore.getState().getPoisByVehicleType(uvt.id);
        const poiCount = pois.filter((poi) => poi.areaId === area.id).length;
        count += poiCount;
      });
      count += rackingAreasAlongside.length;
      result.set(uvt.name, count);
    });

    return result;
  }

  private getMaxCapacityByVehicleType(
    vehicleType: string,
    maxCapacityFromStationaries: number,
  ): number {
    const hasShuttleTemplate = this.fddmStore.getState().design.templates.shuttleRacks.length > 0;

    if (hasShuttleTemplate && VehiclesUtil.isShuttleVehicle(vehicleType)) {
      return this.getShuttleTemplateVehicleCapacity();
    }

    return maxCapacityFromStationaries;
  }

  private getShuttleTemplateVehicleCapacity(): number {
    return this.fddmStore
      .getState()
      .design.templates.shuttleRacks.reduce(
        (capacity, shuttleRack) =>
          capacity + shuttleRack.params.numColumns * shuttleRack.params.numLevels,
        0,
      );
  }

  private getValidVehicleAmount(preferredAmount: number, maxCapacity: number): number {
    if (maxCapacity <= 0) {
      return 0;
    }

    if (preferredAmount < 1) {
      return 1;
    }

    if (preferredAmount > maxCapacity) {
      return maxCapacity;
    }

    return preferredAmount;
  }

  private async displayErrorMessages(errors: SimulationError[]) {
    const messages = await Promise.all(errors.map((simError) => this.getErrorMessage(simError)));

    const singleMessage = messages.join('\n');
    SnackbarServiceFactory.getInstance().createSnackbarService().showMessage(singleMessage);
  }

  private async getErrorMessage(simError: SimulationError): Promise<string> {
    const notification = await ErrorMessageServiceConnector.getInstance().getNotificationDetails(
      simError.code,
    );
    return SimulationUtil.formatErrorMessage(notification.description, simError.args);
  }
}
